import React, { useState, useEffect } from 'react';
import './Contact.css';

const ContactCropDisease = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    services: []
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [submitMessage, setSubmitMessage] = useState('');

  const totalSteps = 5;

  // Page-specific content for Crop Disease Detection
  const content = {
    sectionSubtitle: "A digital agronomist in your pocket — accessible anytime, anywhere",
    leftHeading: "Ready to protect your harvest?",
    leftDescription: "Share your details and we'll help you implement custom AI models trained on your specific crops, regions, and disease patterns. From API integration to white-label solutions, we build what you need.",
    rightHeading: "Smart diagnosis for modern farming",
    buttonText: "Get Started →",
    services: [
      'Crop & Disease Detection',
      'Custom Model Training',
      'API Integration',
      'White-label Solution',
      'On-premise Deployment',
      'Cloud Hosting',
      'Mobile App Integration',
      'Analytics Dashboard',
    ]
  };

  useEffect(() => {
    const checkHash = () => {
      if (window.location.hash === '#contact') {
        setCurrentStep(1);
        setTimeout(() => {
          const contactSection = document.getElementById('contact');
          if (contactSection) {
            contactSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
    };

    const handleOpenContactForm = () => {
      setCurrentStep(1);
    };

    checkHash();
    const timeoutId = setTimeout(checkHash, 300);
    window.addEventListener('hashchange', checkHash);
    window.addEventListener('openContactForm', handleOpenContactForm);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('hashchange', checkHash);
      window.removeEventListener('openContactForm', handleOpenContactForm);
    };
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (submitStatus) {
      setSubmitStatus(null);
      setSubmitMessage('');
    }
  };

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        return formData.name.trim() !== '';
      case 2:
        return formData.email.trim() !== '';
      case 3:
        return formData.phone.trim() !== '';
      case 4:
        return formData.services.length > 0;
      case 5:
        return formData.message.trim() !== '';
      default:
        return false;
    }
  };

  const handleServiceChange = (service) => {
    setFormData(prev => {
      const services = prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service];
      return { ...prev, services };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setSubmitMessage('');

    try {
      const response = await fetch('https://api.fractallogy.com/contact-us', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          subject: 'Crop & Disease Detection Inquiry - ' + formData.services.join(', '),
          message: formData.message
        })
      });

      let data;
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const text = await response.text();
        data = {
          message: text || (response.ok ? 'Message sent successfully' : 'An error occurred')
        };
      }

      const parseErrorMessage = (msg) => {
        if (!msg) return null;
        if (typeof msg === 'string') return msg;
        if (Array.isArray(msg) || typeof msg === 'object') {
          return 'Please fill all the fields.';
        }
        return String(msg);
      };

      if (response.ok) {
        setSubmitStatus('success');
        setSubmitMessage(parseErrorMessage(data.message) || 'Thank you for your message! We will get back to you soon.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
          services: []
        });
        setTimeout(() => {
          setCurrentStep(0);
          setSubmitStatus(null);
          setSubmitMessage('');
        }, 2000);
      } else {
        setSubmitStatus('error');
        setSubmitMessage(parseErrorMessage(data.message) || 'Something went wrong. Please try again later.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
      setSubmitMessage('Unable to send message. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="contact contact--crop" id="contact">
      <div className="contact-header">
        <h2 className="contact-section-heading">Get in Touch</h2>
        <p className="contact-section-subtitle">{content.sectionSubtitle}</p>
      </div>
      <div className="contact-split">
        <div className="contact-left">
          <div className="contact-left-content">
            <h2>{content.leftHeading}</h2>
            <p>{content.leftDescription}</p>
            <button className="begin-button" onClick={() => setCurrentStep(1)}>
              {content.buttonText}
            </button>
          </div>
        </div>

        <div className="contact-right">
          <div className="contact-right-content">
            <h2 className="contact-right-heading">{content.rightHeading}</h2>
            <div className="contact-image-wrapper">
              <img 
                src="/images/plant_contact.png" 
                alt="Smart Agriculture" 
                className="contact-image"
              />
            </div>
          </div>
        </div>
      </div>

      {currentStep > 0 && (
        <div className="form-overlay">
          <div className="contact-card">
            <div className="contact-card-header">
              <button className="close-button" onClick={() => setCurrentStep(0)}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
              <div className="step-dots">
                {[1, 2, 3, 4, 5].map((step) => (
                  <div
                    key={step}
                    className={`step-dot ${currentStep >= step ? 'active' : ''} ${currentStep === step ? 'current' : ''}`}
                    onClick={() => currentStep > 0 && setCurrentStep(step)}
                  ></div>
                ))}
              </div>
            </div>

            <form className="contact-form" onSubmit={handleSubmit}>
              {currentStep === 1 && (
                <div className="form-step">
                  <div className="conversational-question">
                    <h3>Could we have your name please?</h3>
                    <p className="question-hint">We'd love to know who we're working with</p>
                  </div>
                  <div className="form-group">
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Enter your full name"
                      autoFocus
                    />
                  </div>
                  <div className="form-actions">
                    <button type="button" className="form-button next" onClick={handleNext} disabled={!formData.name.trim()}>
                      Continue
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M5 12h14M12 5l7 7-7 7"/>
                      </svg>
                    </button>
                  </div>
                </div>
              )}

              {currentStep === 2 && (
                <div className="form-step">
                  <div className="conversational-question">
                    <h3>What's your email address?</h3>
                    <p className="question-hint">We'll use this to reach out to you</p>
                  </div>
                  <div className="form-group">
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="your.email@example.com"
                      autoFocus
                    />
                  </div>
                  <div className="form-actions">
                    <button type="button" className="form-button prev" onClick={handlePrevious}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 12H5M12 19l-7-7 7-7"/>
                      </svg>
                      Back
                    </button>
                    <button type="button" className="form-button next" onClick={handleNext} disabled={!formData.email.trim()}>
                      Continue
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M5 12h14M12 5l7 7-7 7"/>
                      </svg>
                    </button>
                  </div>
                </div>
              )}

              {currentStep === 3 && (
                <div className="form-step">
                  <div className="conversational-question">
                    <h3>What's your phone number? <span className="required-asterisk">*</span></h3>
                    <p className="question-hint">We'll use this to reach out to you</p>
                  </div>
                  <div className="form-group">
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="Phone number"
                      autoFocus
                    />
                  </div>
                  <div className="form-actions">
                    <button type="button" className="form-button prev" onClick={handlePrevious}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 12H5M12 19l-7-7 7-7"/>
                      </svg>
                      Back
                    </button>
                    <button type="button" className="form-button next" onClick={handleNext} disabled={!isStepValid()}>
                      Continue
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M5 12h14M12 5l7 7-7 7"/>
                      </svg>
                    </button>
                  </div>
                </div>
              )}

              {currentStep === 4 && (
                <div className="form-step">
                  <div className="conversational-question">
                    <h3>What solutions interest you? <span className="required-asterisk">*</span></h3>
                    <p className="question-hint">Select all that apply</p>
                  </div>
                  <div className="services-checkbox-group">
                    {content.services.map((service) => (
                      <label key={service} className="service-checkbox-label">
                        <input
                          type="checkbox"
                          checked={formData.services.includes(service)}
                          onChange={() => handleServiceChange(service)}
                          className="service-checkbox"
                        />
                        <span className="checkbox-custom"></span>
                        <span className="service-text">{service}</span>
                      </label>
                    ))}
                  </div>
                  <div className="form-actions">
                    <button type="button" className="form-button prev" onClick={handlePrevious}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 12H5M12 19l-7-7 7-7"/>
                      </svg>
                      Back
                    </button>
                    <button type="button" className="form-button next" onClick={handleNext} disabled={!isStepValid()}>
                      Continue
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M5 12h14M12 5l7 7-7 7"/>
                      </svg>
                    </button>
                  </div>
                </div>
              )}

              {currentStep === 5 && (
                <div className="form-step">
                  <div className="conversational-question">
                    <h3>Tell us about your agricultural needs</h3>
                    <p className="question-hint">Help us understand your crops, scale, and detection requirements</p>
                  </div>
                  <div className="form-group">
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="6"
                      placeholder="Describe your farm or operation, the crops you grow, common diseases in your region, and how you'd like to use AI-powered detection..."
                      autoFocus
                    ></textarea>
                  </div>
                  {submitStatus && (
                    <div className={`form-message ${submitStatus}`}>
                      {submitMessage}
                    </div>
                  )}
                  <div className="form-actions">
                    <button type="button" className="form-button prev" onClick={handlePrevious}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 12H5M12 19l-7-7 7-7"/>
                      </svg>
                      Back
                    </button>
                    <button 
                      type="submit" 
                      className="form-button submit"
                      disabled={isSubmitting || !formData.message.trim()}
                    >
                      {isSubmitting ? (
                        <>
                          <svg className="spinner" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10" strokeLinecap="round" strokeDasharray="32" strokeDashoffset="32">
                              <animate attributeName="stroke-dasharray" dur="2s" values="0 32;16 16;0 32;0 32" repeatCount="indefinite"/>
                              <animate attributeName="stroke-dashoffset" dur="2s" values="0;-16;-32;-32" repeatCount="indefinite"/>
                            </circle>
                          </svg>
                          Sending...
                        </>
                      ) : (
                        <>
                          Send Message
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="22" y1="2" x2="11" y2="13"/>
                            <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                          </svg>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      )}
    </section>
  );
};

export default ContactCropDisease;
